import { UseQueryResult } from '@tanstack/react-query';
import { useSession } from '../SessionProvider';
import { useTrackedQuery as useQuery } from '@ais-ui/performance-tracking';

import {
  checkForNewEvents,
  EventsResponse,
  getCurrentEvents,
  getEventsForPeriod,
} from './getEvents';
import { convertFieldsToDates } from '@ais-ui/utils';

export const useGetEvents = (): UseQueryResult<EventsResponse> => {
  const { olSession } = useSession();

  return useQuery({
    queryKey: ['getCurrentEvents'],
    queryFn: () => {
      return getCurrentEvents(olSession);
    },
  }, 'events:getCurrent');
};

export const useGetEventsForPeriod = (
  startDateTime: Date | undefined,
  endDateTime: Date | undefined
): UseQueryResult<EventsResponse> => {
  const { olSession } = useSession();

  // Convert dates to strings for stable query keys
  const startKey = startDateTime?.toISOString();
  const endKey = endDateTime?.toISOString();

  const isEnabled = startDateTime !== undefined && endDateTime !== undefined;

  return useQuery({
    queryKey: ['getPeriodEvents', startKey, endKey],
    queryFn: async () => {
      const response = await getEventsForPeriod(
        olSession,
        startDateTime as Date,
        endDateTime as Date
      );

      if (response) {
        convertFieldsToDates(response, [
          'endDateTime',
          'endTime',
          'startDateTime',
          'startTime',
          'events.activated',
          'events.act_ts',
          'events.deactivated',
          'events.deact_ts',
          'events.ack_state_modified_ts',
        ]);
      }

      return response;
    },
    enabled: isEnabled,
    placeholderData: (previousData) => previousData,
  }, 'events:getForPeriod');
};

export const useCheckForNewEvents = () => {
  const { olSession } = useSession();
  return useQuery({
    queryKey: ['checkForNewEvents'],
    queryFn: () => checkForNewEvents(olSession),
    refetchInterval: 15000,
  }, 'events:checkNew');
};
